<template>
    <div :class="`star-rating-${localAlign} star-rating`">
        <label for="rating" @click="focusFieldLabel" v-if="!editingFieldLabel">{{ displayFieldLabel }}</label>
        <input
            v-if="editingFieldLabel"
            type="text"
            class="field-label-input"
            placeholder="Bir soru girin"
            v-model="localFieldLabel"
            @blur="stopEditingFieldLabel"
            @keydown.enter="stopEditingFieldLabel"
            ref="fieldLabelInput"
        />
        <div class="rating-container">
            <div class="stars">
                <span
                    v-for="(star, index) in 5"
                    :key="index"
                    :class="['star', { 'rated': index < rating, 'hovered': index < hoveredRating }]"
                    @click="setRating(index + 1)"
                    @mouseenter="setHoveredRating(index + 1)"
                    @mouseleave="clearHoveredRating">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                </span>
            </div>
            <div class="rating-text" v-if="rating > 0">
                <span class="rating-value">{{ rating }}</span>
                <span class="rating-label">/ 5</span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
    name: "form-star-rating",
    props: {
        fieldLabel: String,
        align: String,
        item: Object,
    },
    data() {
        return {
            localFieldLabel: this.fieldLabel || "",
            localAlign: this.align || "top",
            rating: 0,
            hoveredRating: 0,
            editingFieldLabel: false,
            updateTimer: null,
        };
    },
    computed: {
        displayFieldLabel() {
            return this.localFieldLabel || "Bir soru girin";
        },
    },
    methods: {
        ...mapActions("forms", ["updateFormField"]),
        focusFieldLabel() {
            this.editingFieldLabel = true;
            this.$nextTick(() => {
                if (this.$refs.fieldLabelInput) {
                    this.$refs.fieldLabelInput.focus();
                }
            });
        },
        stopEditingFieldLabel() {
            this.editingFieldLabel = false;
            this.updateField();
        },
        setRating(value) {
            this.rating = value;
            this.updateField();
        },
        setHoveredRating(value) {
            this.hoveredRating = value;
        },
        clearHoveredRating() {
            this.hoveredRating = 0;
        },
        updateField() {
            let self = this;
            if (this.updateTimer) {
                clearTimeout(this.updateTimer);
            }
            this.updateTimer = setTimeout(function () {
                const updateFields = {
                    starRating: {
                        fieldLabel: self.localFieldLabel,
                        rating: self.rating,
                        align: self.localAlign,
                    },
                };
                self.updateFormField({
                    siteId: self.item.siteId,
                    formFieldId: self.item._id,
                    updateFields: updateFields,
                });
                self.$emit("update-field", updateFields);
            }, 200);
        },
    },
    watch: {
        fieldLabel(newVal) {
            this.localFieldLabel = newVal;
        },
        align(newVal) {
            this.localAlign = newVal;
        },
        'item.starRating.rating': function (v) {
            if (typeof v === 'number') this.rating = v;
        },
        'item.starRating.fieldLabel': function (v) { this.localFieldLabel = v; },
    },
};
</script>

<style lang="scss" scoped>
.star-rating {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    background: linear-gradient(145deg,
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s ease;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

    &:hover {
        background: linear-gradient(145deg,
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    > label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin: 0 0 clamp(0.5rem, 1.2vw, 0.75rem) 0;
        color: rgba(55, 65, 81, 0.9);
        line-height: 1.4;
        cursor: pointer;
    }

    .field-label-input {
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        color: rgba(55, 65, 81, 0.9);
        line-height: 1.4;
        border: none;
        background: transparent;
        outline: none;
        border-bottom: 2px solid #f26a52;
        padding: 0 0 2px 0;

        &::placeholder {
            color: rgba(156, 163, 175, 0.7);
        }
    }

    .rating-container {
        background: rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(8px);
        border: 2px solid rgba(209, 213, 219, 0.6);
        border-radius: clamp(0.5rem, 1.5vw, 0.75rem);
        padding: clamp(1rem, 2.5vw, 1.25rem);
        transition: all 0.3s ease;

        &:hover {
            border-color: #f26a52;
            box-shadow: 0 4px 12px rgba(242, 106, 82, 0.15);
        }
    }

    .stars {
        display: flex;
        gap: clamp(0.375rem, 1vw, 0.5rem);
        align-items: center;
        justify-content: flex-start;
        margin-bottom: clamp(0.625rem, 1.5vw, 0.75rem);

        .star {
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            width: clamp(2rem, 4.5vw, 2.25rem);
            height: clamp(2rem, 4.5vw, 2.25rem);
            border-radius: 50%;

            svg {
                width: clamp(1.5rem, 3.5vw, 1.75rem);
                height: clamp(1.5rem, 3.5vw, 1.75rem);
                color: #d1d5db;
                transition: all 0.3s ease;
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
            }

            &:hover {
                background: rgba(255, 193, 7, 0.1);
                transform: scale(1.1);

                svg {
                    transform: rotate(10deg);
                }
            }

            &.hovered svg,
            &.rated svg {
                color: #ffc107;
                transform: scale(1.05);
            }

            &.rated {
                &:hover {
                    background: rgba(255, 193, 7, 0.15);
                }

                svg {
                    filter: drop-shadow(0 3px 6px rgba(255, 193, 7, 0.3));
                }
            }

            &.rated::after {
                content: '';
                position: absolute;
                width: 4px;
                height: 4px;
                background: #fff;
                border-radius: 50%;
                top: 8px;
                right: 10px;
                opacity: 0.8;
                animation: sparkle 2s infinite;
            }
        }
    }

    .rating-text {
        display: flex;
        align-items: center;
        gap: clamp(0.375rem, 1vw, 0.5rem);
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        color: rgba(107, 114, 128, 0.8);
        justify-content: center;

        .rating-value {
            font-size: clamp(1rem, 2.2vw, 1.125rem);
            font-weight: 600;
            color: #ffc107;
            text-shadow: 0 1px 2px rgba(255, 193, 7, 0.3);
        }

        .rating-label {
            font-weight: 500;
        }
    }
}

@keyframes sparkle {
    0%, 100% {
        opacity: 0.8;
        transform: scale(1);
    }
    50% {
        opacity: 1;
        transform: scale(1.2);
    }
}

/* Alignment Classes */
.star-rating-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label, .field-label-input {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: left;
    }

    .rating-container {
        flex: 1;
        min-width: 0;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;

        > label, .field-label-input {
            width: 100%;
            text-align: left;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }

        .rating-container {
            width: 100%;
        }
    }
}

.star-rating-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    > label, .field-label-input {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.6rem, 1.5vw, 0.75rem);
        text-align: right;
    }

    .rating-container {
        flex: 1;
        min-width: 0;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;

        > label, .field-label-input {
            width: 100%;
            text-align: left;
            padding-top: 0;
            margin-bottom: clamp(0.5rem, 1.2vw, 0.75rem);
        }

        .rating-container {
            width: 100%;
        }
    }
}

.star-rating-top {
    > label {
        text-align: left;
    }
}

@media (max-width: 768px) {
    .star-rating {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);

        .rating-container {
            padding: clamp(0.875rem, 2vw, 1rem);
            border-radius: clamp(0.375rem, 1vw, 0.5rem);
        }

        .stars {
            gap: clamp(0.25rem, 0.8vw, 0.375rem);
            justify-content: center;

            .star {
                width: clamp(1.75rem, 4vw, 2rem);
                height: clamp(1.75rem, 4vw, 2rem);

                svg {
                    width: clamp(1.25rem, 3vw, 1.5rem);
                    height: clamp(1.25rem, 3vw, 1.5rem);
                }

                &:hover {
                    transform: scale(1.05);
                }
            }
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .star-rating, .star, .rating-container {
        transition: none;
    }

    .star:hover, .star.rated::after {
        transform: none;
        animation: none;
    }
}
</style>
